/* Low-stock indicator next to the PDP size selector.

   sections/main-product.liquid is too large to safely rewrite wholesale (established
   convention in this theme -- see pdp-support-line.js, theme-events-patch.js and
   theme-wish-heart-fix.js), so this ships as an independent, idempotent patch instead of
   touching that file.

   Hooking into selection changes: sections/main-product.liquid's own inline script keeps the
   "Add to bag" buttons' `data-variant` attribute in sync with whichever variant is currently
   selected (color + size combined) every time the shopper picks a new option -- see its
   update()/updateLabels() function. A MutationObserver on that attribute is a decoupled way to
   know "the shopper just picked a different size" without re-implementing the section's own
   option-matching logic, and it works no matter which UI path changed the variant (size tap,
   a color swap that shifts availability, the "Find my size" quiz, the sticky bar popover, etc).

   IMPORTANT correction #2 (found via live click-through repro on JESUS Denim Pants -- selecting
   a genuinely sold-out size, e.g. M, kept showing the PREVIOUSLY selected size's stale
   "Only 3 left in your size" message instead of hiding): this file used to gate on
   `addBtn.classList.contains('notify')` as the "authoritative, always-fresh" signal for a
   sold-out combo. That class name is stale -- it was written for an earlier revision of
   sections/main-product.liquid that had a "Notify me" flow; that flow was permanently removed
   (see the section's own updateLabels(), which now only ever toggles a `soldout` class and sets
   `btn.disabled`, never `notify`). Because 'notify' can never match anymore, this check silently
   never fired, so a sold-out selection fell straight through to the stale `data-variant` lookup
   below (which is exactly the failure mode the very next paragraph describes) instead of being
   short-circuited. Fixed by checking the button's actual live `disabled` state / `soldout` class
   instead -- both are set unconditionally on every single selection change by the section's own
   updateLabels(), available or not, so they can never go stale the way data-variant can.

   sections/main-product.liquid's updateLabels() only rewrites data-variant when the newly-selected
   variant is ACTUALLY AVAILABLE -- literally `if(cv && cv.available) btn.setAttribute('data-variant', cv.id);`.
   For an unavailable/sold-out combo it deliberately leaves data-variant untouched, so the attribute
   keeps pointing at whatever variant id was last available. That means data-variant can never
   be trusted on its own to answer "what is currently selected" -- it only reliably answers
   "what was last selected AND in stock". `addBtn.disabled`/`.soldout`, by contrast, ARE updated
   unconditionally on every single selection change, so that's what gates the message now.

   Two Add buttons, not one: the section renders a second, cloned set of option groups
   (`.pdp-optgroup`) inside the sticky add-to-bag bar's popover for quick selection without
   scrolling back up, alongside a second `[data-add]` button in that sticky bar. Both buttons
   are driven by the SAME shared `selected`/`groups` state and are updated together, synchronously,
   inside the section's single delegated click handler + update() call (its own comment there
   reads "delegated: covers main + popover clones") -- so a tap on either the main size chips or
   the sticky popover's cloned chips ends up flipping both buttons' soldout class and data-variant
   identically in the same tick. Both buttons are still watched explicitly below (rather than
   assuming the main one is always first-to-update) so this stays correct even if that
   synchronization ever changes.

   Inventory source: window.YG.inventory, populated in layout/theme.liquid with each variant's
   REAL available/inventory_management/inventory_quantity/inventory_policy, built with direct
   Liquid field access on each variant. This was verified necessary by direct testing: neither
   the section's own embedded <script data-variants>{{ product.variants | json }}</script> blob
   nor the public /products/<handle>.js endpoint (the fetch() pattern used elsewhere in this
   theme, e.g. theme-events-patch.js) actually include inventory_quantity/inventory_policy on
   this store -- Shopify's json filter and that AJAX endpoint both omit them, even though the
   fields are readable directly off each variant in Liquid. So the real numbers are rendered
   explicitly in the layout (see the window.YG.inventory comment there) rather than faked here.
   fetch('/products/<handle>.js') is kept as a last-resort fallback purely for structural
   parity with the rest of the theme and in case a future storefront change restores those
   fields on that endpoint -- it is never used to invent a number, and if it can't supply real
   inventory data the indicator simply stays hidden.

   Threshold: window.YG.lowStockThreshold, set in layout/theme.liquid from the merchant-facing
   Theme Settings > Product page > "Low stock warning threshold" range control. Falls back to 5
   if that global is somehow missing (e.g. a very old cached page). NOTE: this is a theme-wide
   setting (config/settings_schema.json / settings_data.json) -- NOT the same as the unrelated
   `low_stock_threshold` field on the main-product SECTION schema (the one behind the section's
   own `data-low` attribute on <section class="pdp ...">). That section-level field feeds the
   section's OWN inline low-stock badge (`.pdp-badge`/`data-badge`/updateBadge() in
   sections/main-product.liquid) -- which, on this store, never actually renders anything: it
   depends on `stockOf()` reading `inventory_quantity` off the embedded
   `<script data-variants>{{ product.variants | json }}</script>` blob, and (per the paragraph
   above) that field is simply absent from Shopify's `json` filter output here, so that inline
   badge's count is always `null` and it stays hidden. This file is the one actually rendering
   the low-stock pill shoppers see. Left the section's dead inline badge alone on purpose (see
   the section-file-size note above) since it's inert and touching that large file for a no-op
   removal isn't worth the risk.

   Badge style: rendered as a small chip/tag -- rounded, subtle ivory-2 fill with a gold-tinted
   border + dot -- via ensureStyle() below. Sentence-case text (not uppercase) so a short "N
   left" string reads as a compact status chip rather than a stretched-out uppercase label.

   Placement (correction -- found via live mobile screenshot review): this used to be inserted
   as its own block-level <div> immediately after the whole size-selector option group, which
   put it on its own separate row below the size chips and, on narrow viewports, left it
   sitting flush left instead of reading as part of the centered "SIZE — value" line above it.
   It now renders as an inline <span> appended directly inside that option group's own
   `.pdp-cap` label div (right after the "SIZE — value" text), so it sits on the SAME visual
   line as the label -- centered/left/right exactly however that label itself is aligned on a
   given breakpoint -- and wraps naturally onto a second line only if the combined text doesn't
   fit, rather than always forcing its own row. sections/main-product.liquid's `.pdp-cap` rule
   is itself `display:flex;align-items:center;flex-wrap:wrap;gap:8px` for exactly this reason,
   so the "SIZE — value" text and this badge lay out as flex siblings on one row and only wrap
   if a given breakpoint genuinely can't fit both.

   Text (correction -- merchant feedback: the previous "Only N left in your size" wording was
   too long to ever comfortably sit beside the "SIZE — value" label on mobile, so it kept
   wrapping to its own line -- exactly the layout the placement fix above was meant to avoid.
   Shortened to just "N left", e.g. "2 left" / "1 left" -- short enough to always fit beside
   even a long label like "SIZE — XL (40-42)"): shows the real tracked count whenever one is
   confidently known ("N left"). Never fabricates a specific number. The one exception where a
   generic "Low stock" label (no number) is shown instead of hiding entirely: a variant that IS
   tracked (inventory_management === 'shopify') and IS available, but whose real
   inventory_quantity is at or below zero -- only possible when inventory_policy is 'continue'
   (oversell/backorder allowed), so Shopify still treats it as purchasable. 0 or a negative
   number isn't a sensible "N left" figure to print, but the underlying signal (tracked, real,
   at-or-under-zero) is genuine and low, so the generic label is honest here rather than a
   fabrication. Every other case where a confident number isn't available (inventory not
   tracked at all, variant missing from the inventory map, etc.) still hides the indicator
   entirely, since there is no real signal at all in that case to base even a generic message on.

   Idempotent + re-runs after every soft-nav content swap (yg:contentswap), same pattern as
   pdp-support-line.js. */
(function(){
  function threshold(){
    var t = window.YG && window.YG.lowStockThreshold;
    return (typeof t === 'number' && t > 0) ? t : 5;
  }

  function ensureStyle(){
    if(document.getElementById('pdp-low-stock-style')) return;
    /* Small chip/tag badge -- rounded, subtle ivory-2 fill with a gold-tinted border and dot --
       sized and cased to read as a genuinely small status chip now that the text itself is just
       "N left" rather than a full sentence. Renders inline (see ensureMsgEl below) so it sits on
       the same line as the "SIZE — value" label instead of forming a separate row underneath it;
       the parent `.pdp-cap` rule (sections/main-product.liquid) is `display:flex;flex-wrap:wrap`
       so this chip and the label text lay out as flex siblings on one row. */
    var css = '.pdp-lowstock{display:none;align-items:center;gap:4px;font-size:10px;font-weight:600;text-transform:none;color:var(--ink);background:var(--ivory-2);border:1px solid rgba(185,154,91,.5);border-radius:20px;padding:2px 8px;margin:0;vertical-align:middle;width:fit-content;flex:0 0 auto}'+
      '.pdp-lowstock.show{display:inline-flex}'+
      '.pdp-lowstock::before{content:"";width:4px;height:4px;border-radius:50%;background:var(--gold);box-shadow:0 0 0 2px rgba(185,154,91,.18);flex:0 0 auto}'+
      '.pdp-lowstock span{color:var(--ink)}';
    var s = document.createElement('style');
    s.id = 'pdp-low-stock-style';
    s.textContent = css;
    document.head.appendChild(s);
  }

  function ensureMsgEl(root, form){
    var el = root.querySelector('.pdp-lowstock');
    if(el) return el;
    /* Inline <span> (not a block <div>) so it can live inside the label's own .pdp-cap div and
       sit on the same text line as "SIZE — value" -- see file header "Placement" note. */
    el = document.createElement('span');
    el.className = 'pdp-lowstock';
    el.setAttribute('role', 'status');
    var span = document.createElement('span');
    el.appendChild(span);
    var sizeGroups = [].slice.call(form.querySelectorAll('.pdp-optgroup:not(.is-color)'));
    var anchor = sizeGroups.length ? sizeGroups[sizeGroups.length - 1] : null;
    var cap = anchor ? anchor.querySelector('.pdp-cap') : null;
    if(cap){
      cap.appendChild(el);
    } else if(anchor){
      anchor.insertAdjacentElement('afterend', el);
    } else {
      var addrow = form.querySelector('.pdp-addrow');
      if(addrow) addrow.insertAdjacentElement('beforebegin', el);
      else return null; /* no size selector on this template -- nothing to anchor to */
    }
    return el;
  }

  function setMessage(el, val){
    if(!el) return;
    if(val == null){ el.classList.remove('show'); return; }
    if(val === 'low'){
      /* Generic fallback -- see file header: only used when we have a real (tracked) signal
         that stock is at/under zero but printing that number would be nonsensical. */
      el.querySelector('span').textContent = 'Low stock';
    } else {
      /* Short "N left" chip text -- see file header "Text" note. No special-casing needed for
         singular vs plural: "1 left" and "2 left" both read correctly as-is. */
      el.querySelector('span').textContent = val + ' left';
    }
    el.classList.add('show');
  }

  function readVariantsFromDom(root){
    var script = root.querySelector('[data-variants]');
    if(!script) return null;
    try {
      var list = JSON.parse(script.textContent);
      var byId = {};
      list.forEach(function(v){ if(v && v.id != null) byId[String(v.id)] = v; });
      return byId;
    } catch(e){ return null; }
  }

  function init(root){
    /* Only products with a real size/variant selector have anything to show -- single-variant
       templates have no .pdp-form option groups at all. */
    var form = root.querySelector('[data-form]');
    var addBtn = root.querySelector('.pdp-addrow [data-add]');
    if(!form || !addBtn) return;

    if(!form.querySelector('.pdp-optgroup:not(.is-color)')) return;

    ensureStyle();
    var msgEl = ensureMsgEl(root, form);
    if(!msgEl) return;

    /* The sticky add-to-bag bar renders its own [data-add] button (kept in sync with the main
       one by the section's own script -- see file header). Watched alongside the main button so
       this stays correct even if a shopper's interaction only visibly touches the sticky
       popover's cloned option chips. */
    var stickyAddBtn = root.querySelector('[data-add][data-sticky]');
    var watchedBtns = (stickyAddBtn && stickyAddBtn !== addBtn) ? [addBtn, stickyAddBtn] : [addBtn];

    /* Primary source: real inventory rendered by layout/theme.liquid for this product. */
    var byId = (window.YG && window.YG.inventory && Object.keys(window.YG.inventory).length) ? window.YG.inventory : null;
    /* Secondary source: the section's own embedded variant JSON (may lack inventory fields,
       see file header, but still checked in case a future Shopify change restores them). */
    if(!byId) byId = readVariantsFromDom(root);

    function apply(){
      /* Authoritative check first: never show the low-stock message while the section itself has
         decided the current combo is unpurchasable (sold-out size, sold-out color, etc). `disabled`
         and the `soldout` class are both updated unconditionally on EVERY selection change by the
         section's own updateLabels() (unlike data-variant, see file header), so they can't go
         stale the way a variant-id lookup can. `notify` is also checked for forward-compatibility
         in case that flow is ever reintroduced, but disabled/.soldout are the real, current signal. */
      if(addBtn.disabled || addBtn.classList.contains('soldout') || addBtn.classList.contains('notify')){ setMessage(msgEl, null); return; }

      var vid = addBtn.getAttribute('data-variant');
      var v = (byId && vid) ? byId[vid] : null;
      if(!v || v.available === false){ setMessage(msgEl, null); return; }
      if(v.inventory_management !== 'shopify'){ setMessage(msgEl, null); return; }
      var qty = v.inventory_quantity;
      if(typeof qty !== 'number'){ setMessage(msgEl, null); return; }
      /* Tracked + available (per the checks above), but at or under zero units -- only possible
         with inventory_policy 'continue' (oversell/backorder allowed). A literal number here
         (0 or negative) wouldn't be a sensible "X left" claim, but the tracked-and-thin signal
         is real, so show the generic label rather than either hiding or fabricating a count. */
      if(qty <= 0){ setMessage(msgEl, 'low'); return; }
      if(qty > threshold()){ setMessage(msgEl, null); return; }
      setMessage(msgEl, qty);
    }

    if(root._lowStockObserver) root._lowStockObserver.disconnect();
    var mo = new MutationObserver(apply);
    watchedBtns.forEach(function(btn){
      /* 'class' is watched in addition to 'data-variant' so a switch into/out of the disabled/
         soldout state is caught even on the (sold-out-combo) path where data-variant itself
         doesn't change -- see file header for why data-variant alone isn't a reliable signal. */
      mo.observe(btn, { attributes: true, attributeFilter: ['data-variant', 'class', 'disabled'] });
    });
    root._lowStockObserver = mo;

    if(byId){
      apply();
      return;
    }

    /* Last-resort fallback -- see file header. */
    var wishBtn = root.querySelector('[data-wish-handle]');
    var handle = wishBtn && wishBtn.getAttribute('data-wish-handle');
    if(!handle) return;
    fetch('/products/' + handle + '.js')
      .then(function(r){ return r.ok ? r.json() : null; })
      .then(function(p){
        if(!p || !p.variants) return;
        byId = {};
        p.variants.forEach(function(v){ byId[String(v.id)] = v; });
        apply();
      })
      .catch(function(){});
  }

  function initAll(root){
    (root || document).querySelectorAll('.pdp-editorial').forEach(init);
  }

  initAll();
  document.addEventListener('yg:contentswap', function(e){ initAll((e.detail && e.detail.root) || document); });
})();
